// src/features/eventos/domain/generateLicenciaWord.js

import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";

/* =======================
   Helpers de fecha
======================= */

/**
 * Parsea un date-like (String ISO, String YYYY-MM-DD o Date object)
 * devolviendo un Date object en hora local si no trae componente T.
 */
function parseDate(dateLike) {
    if (!dateLike) return null;
    if (dateLike instanceof Date) return dateLike;

    const s = String(dateLike).trim();
    if (!s) return null;

    // Si ya incluye "T", confiamos en el constructor nativo (ISO format)
    if (s.includes("T")) {
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
    }

    // Si es formato YYYY-MM-DD simple, forzamos a medianoche LOCAL para evitar saltos de día por TZ
    // El constructor new Date("2023-10-27") a veces se interpreta como UTC, restando un día en España (GMT+1/2) si es medianoche
    const d = new Date(`${s}T00:00:00`);
    return isNaN(d.getTime()) ? null : d;
}

function toFechaLargaES(dateLike) {
    const d = parseDate(dateLike);
    if (!d) return "";
    return d.toLocaleDateString("es-ES", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

function toFechaCortaES(dateLike) {
    const d = parseDate(dateLike);
    if (!d) return "";
    return d.toLocaleDateString("es-ES");
}

function safeStr(v) {
    return v == null ? "" : String(v).trim();
}

/* =======================
   Helpers dominio
======================= */

// Detecta rol CAZADOR (string u objeto)
function isCazador(pc) {
    const r = pc?.rol;
    if (!r) return false;
    if (typeof r === "string") return r === "CAZADOR";
    if (typeof r === "object") return r.rol === "CAZADOR";
    return false;
}

// Intenta sacar una fecha válida de cualquier objeto
function pickDate(obj) {
    return (
        obj?.fechaCaducidad ??
        obj?.fechaDeCaducidad ??
        obj?.fechaDeExpedicion ??
        obj?.fechaValidez ??
        obj?.fechaFin ??
        null
    );
}

// Devuelve la fecha más reciente (formato corto ES)
function maxDateToCortaES(list) {
    const dates = (list || [])
        .map((x) => pickDate(x))
        .filter(Boolean)
        .map((x) => parseDate(x))
        .filter((d) => d && !Number.isNaN(d.getTime()))
        .sort((a, b) => b.getTime() - a.getTime());

    if (!dates.length) return "—";
    // Corregido: ya no usamos .toISOString().slice(0, 10) que causaba el error de "un día menos"
    return toFechaCortaES(dates[0]);
}

/* =======================
   BUILDER PRINCIPAL
   (por CACERÍA)
======================= */

export function buildSolicitudLicenciaDataFromCaceria({ caceria, fechaFirma }) {
    // 1️⃣ Jornada (UNA por documento)
    const jornadas = caceria?.fecha
        ? [
            {
                fecha: toFechaCortaES(caceria.fecha),
                modalidad: safeStr(caceria.tipoCaceria) || "—",
            },
        ]
        : [];

    // 2️⃣ Participaciones → solo CAZADORES
    const parts = Array.isArray(caceria?.participaciones) ? caceria.participaciones : [];

    const cazadores = parts
        .filter(isCazador)
        .map((pc) => pc?.persona)
        .filter(Boolean)
        .map((per) => {
            const nombreCompleto =
                [per?.nombre, per?.apellidos].filter(Boolean).join(" ").trim() ||
                safeStr(per?.nickname) ||
                "—";

            const pais = safeStr(per?.pais) || "—";
            const documentoNumero = safeStr(per?.numeroDocumento) || "—";

            // Documento / pasaporte: priorizamos fechaCaducidad que es la que viene en el log
            const fPas = per?.fechaCaducidad || per?.fechaDeCaducidad;
            const pasaporteExpiracion = fPas ? toFechaCortaES(fPas) : "—";

            // Licencias de caza
            const licenciaOrigenExpiracion = maxDateToCortaES(per?.licenciasCaza || []);

            // Seguros
            const seguroExpiracion = maxDateToCortaES(per?.seguros || []);

            return {
                nombreCompleto,
                pais,
                documentoNumero,
                pasaporteExpiracion,
                licenciaOrigenExpiracion,
                seguroExpiracion,
                // Fallbacks y alias extra para la plantilla Word
                fechaCaducidad: pasaporteExpiracion,
                pasaporte_caducidad: pasaporteExpiracion,
                licencia_caducidad: licenciaOrigenExpiracion,
                seguro_caducidad: seguroExpiracion,
                seguro_fechaDeCaducidad: seguroExpiracion
            };
        });

    // 3️⃣ Resultado final para Docxtemplater
    const finalData = {
        jornadas,
        cazadores,
        fechaFirmaLarga: toFechaLargaES(fechaFirma),
    };

    console.log(">>> DATOS FINALES PARA DOCX:", finalData);

    return finalData;
}

/* =======================
   GENERADOR DOCX
======================= */

export async function generateSolicitudLicenciaDocx({
    templateUrl,
    data,
    fileName = "Solicitud_licencia_caza.docx",
}) {
    const res = await fetch(templateUrl);
    if (!res.ok) {
        throw new Error(`No se pudo cargar la plantilla: ${templateUrl}`);
    }

    const arrayBuffer = await res.arrayBuffer();
    const zip = new PizZip(arrayBuffer);

    const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
    });

    doc.setData(data);

    try {
        doc.render();
    } catch (e) {
        console.error("DOCX render error", e);
        throw e;
    }

    const out = doc.getZip().generate({
        type: "blob",
        mimeType:
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    saveAs(out, fileName);
}
